<template>
  <li class="flex items-center justify-between px-4 py-3 text-xs text-off-black bg-dusky-grey">
    Recently used addresses
  </li>
  <li
    v-for="(saved, index) in addressWithId"
    :key="saved.id"
    class="border-b border-sandstone hover:bg-light-linen hover:border-0 bg-extra-light-linen"
  >
    <button
      class="relative flex items-center justify-between w-full h-16 px-4 py-3 cursor-pointer"
      role="option"
      aria-selected="false"
      :data-index="index"
      @click.prevent="handleSuggestionPress({ suggestion: saved.suggestion, id: saved.id })"
    >
      <div class="flex items-center">
        <div>
          <LocationMarkerIcon class="mr-2 text-dark-linen size-5" />
        </div>
        <div class="flex flex-col items-start">
          <p class="text-sm text-left text-off-black lg:text-base">
            {{ saved.suggestion.description }}
          </p>
        </div>
      </div>
    </button>
  </li>
</template>

<script setup lang="ts">
  import usePreviousSuggestions, {
    ManualAddressInputWithDescription,
  } from '@/composables/usePreviousSuggestions';
  import { trackCustomEvent } from '@/utils/analytics';
  import { LocationMarkerIcon } from '@heroicons/vue/solid';
  import dayjs from 'dayjs';

  interface Emits {
    (
      e: 'savedAddressPress',
      value: { suggestion: ManualAddressInputWithDescription; id: string }
    ): void;
  }

  const emit = defineEmits<Emits>();

  const { savedSuggestions } = usePreviousSuggestions();

  const addressWithId = computed(() => {
    if (!savedSuggestions.value) return [];
    return (
      Object.entries(savedSuggestions.value)
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        .sort(([_, savedSuggestionA], [__, savedSuggestionB]) => {
          const diffInMilis = dayjs(savedSuggestionA.timestamp).diff(
            dayjs(savedSuggestionB.timestamp)
          );
          return diffInMilis > 0 ? -1 : 1;
        })
        .map(([id, savedSuggestion]) => {
          return {
            id,
            suggestion: savedSuggestion.address,
          };
        })
    );
  });

  interface HandleSuggestionPressParams {
    suggestion: ManualAddressInputWithDescription;
    id: string;
  }

  const handleSuggestionPress = ({ suggestion, id }: HandleSuggestionPressParams) => {
    trackCustomEvent('CTA_clicked', {
      click_id: 'saved_address_pressed',
      click_text: 'Saved Address Pressed',
      click_url: window.location.href,
    });
    emit('savedAddressPress', {
      suggestion,
      id,
    });
  };
</script>
